<!--
  TrackerMapLeaflet.vue — the map on the REAL go.lacak.io engine: Leaflet 1.9.4 + Google tiles
  via GoogleMutant + PruneCluster (canvas-style donut) + leaflet-hotline (history track) +
  leaflet-ant-path (routes). This is the DEFAULT map engine (Google JS API TrackerMap.vue is the
  ?engine=google fallback); it's a drop-in — same props/emits/methods. Feature-complete: basemap
  switcher, state-shape markers + clustering, tails/animation, overlays (geofences/layers/places),
  interaction tools (search/measure/route/favourites/add-place/reference-pick), and full history
  playback (hotline speed track, colour modes, chevrons, teardrop pins, accuracy circles, LBS runs).
  Reconstructed from lacak.io deployed behaviour; internal impl re-authored. See docs/MAP-TECH.md.
-->
<template>
  <div class="lmap-root" :class="{ picking: pickingRef || mode }">
    <div ref="map" class="lmap"></div>

    <!-- reference-point pick bar (driven by the Objects panel "sort by distance") -->
    <div v-if="pickingRef" class="lmode-bar">Click the map to set the reference point<button class="lmode-x" @click="pickingRef=false"><i class="material-icons">close</i></button></div>
    <div v-if="mode==='measure'" class="lmode-bar">Measure: <b>{{ measureText }}</b> · click points<button class="lmode-x" @click="setMode(null)"><i class="material-icons">close</i></button></div>
    <div v-if="mode==='route'" class="lmode-bar">Route: click <b>{{ routePts.length }}/2</b> points<span v-if="routeText"> · {{ routeText }}</span><button class="lmode-x" @click="setMode(null)"><i class="material-icons">close</i></button></div>
    <div v-if="mode==='poi'" class="lmode-bar">Click the map to drop a place<button class="lmode-x" @click="setMode(null)"><i class="material-icons">close</i></button></div>

    <!-- add-place form (centre) — appears after clicking the map in add-place mode -->
    <div v-if="poiForm" class="lpoi-form">
      <div class="lpoi-form-head"><i class="material-icons">edit_location</i>New place</div>
      <input class="mp-input" v-model="poiForm.name" type="text" placeholder="Place name" />
      <div class="lpoi-coord">{{ poiForm.lat.toFixed(5) }}, {{ poiForm.lng.toFixed(5) }}</div>
      <div class="lpoi-actions"><button class="poi-cancel" @click="cancelPoi">Cancel</button><button class="poi-save" @click="savePoi">Save</button></div>
    </div>

    <!-- map control cluster (top-right): search · add-place · geofences · layers · basemap · fit -->
    <div class="map-ctrl map-ctrl-top">
      <button class="mc-btn" :class="{ on: panel==='search' }" v-tip="'Search'" @click="togglePanel('search')"><i class="material-icons">search</i></button>
      <button class="mc-btn" :class="{ on: mode==='poi' }" v-tip="'Add place'" @click="setMode('poi')"><i class="material-icons">edit_location</i></button>
      <button class="mc-btn" :class="{ on: panel==='geofences' }" v-tip="'Geofences'" @click="togglePanel('geofences')"><i class="material-icons">format_shapes</i></button>
      <button class="mc-btn" :class="{ on: panel==='layers' }" v-tip="'Layers'" @click="togglePanel('layers')"><i class="material-icons">layers</i></button>
      <button class="mc-btn" :class="{ on: panel==='map' }" v-tip="'Basemap'" @click="togglePanel('map')"><i class="material-icons">map</i></button>
      <button class="mc-btn" v-tip="'Fit all objects'" @click="fitAll"><i class="material-icons">zoom_out_map</i></button>
    </div>

    <!-- mid group: route · measure · favourites -->
    <div class="map-ctrl map-ctrl-mid">
      <button class="mc-btn" :class="{ on: mode==='route' }" v-tip="'Route'" @click="setMode('route')"><i class="material-icons">swap_calls</i></button>
      <button class="mc-btn" :class="{ on: mode==='measure' }" v-tip="'Measure distance'" @click="setMode('measure')"><i class="material-icons">straighten</i></button>
      <button class="mc-btn" :class="{ on: panel==='fav' }" v-tip="'Favorites'" @click="togglePanel('fav')"><i class="material-icons">star_border</i></button>
    </div>

    <!-- bottom-right: styled zoom + fullscreen (matches go.lacak.io; Leaflet's default zoomControl
         is disabled so these carry the app's look + v-tip tooltips like the Google engine) -->
    <div class="map-ctrl map-ctrl-bottom">
      <button class="mc-btn" v-tip="'Zoom in'" @click="zoomBy(1)"><i class="material-icons">add</i></button>
      <button class="mc-btn" v-tip="'Zoom out'" @click="zoomBy(-1)"><i class="material-icons">remove</i></button>
      <button class="mc-btn" v-tip="'Fullscreen'" @click="toggleFullscreen"><i class="material-icons">fullscreen</i></button>
    </div>

    <div v-if="panel==='map'" class="map-panel">
      <div class="mp-head"><i class="material-icons">map</i><span>Map</span><button class="mp-x" v-tip="'Close'" @click="panel=null"><i class="material-icons">close</i></button></div>
      <label v-for="b in baseLayers" :key="b.key" class="mp-row" @click="setBase(b.key)">
        <span class="mp-radio" :class="{ sel: base===b.key }"></span>{{ b.label }}
      </label>
      <div class="mp-sub">Overlays</div>
      <label class="mp-check"><input type="checkbox" :checked="overlays.traffic" @change="toggleOverlay('traffic')" /> Traffic</label>
      <label class="mp-check"><input type="checkbox" :checked="overlays.transit" @change="toggleOverlay('transit')" /> Transit</label>
      <label class="mp-check"><input type="checkbox" :checked="overlays.bike" @change="toggleOverlay('bike')" /> Bicycling</label>
    </div>

    <!-- Geofences panel — toggle the zone/list shapes (circle / corridor / polygon) -->
    <div v-if="panel==='geofences'" class="map-panel">
      <div class="mp-head"><i class="material-icons">format_shapes</i><span>Geofences</span><button class="mp-x" v-tip="'Close'" @click="panel=null"><i class="material-icons">close</i></button></div>
      <label v-for="z in geofences" :key="z.id" class="mp-check"><input type="checkbox" :checked="!!activeZones[z.id]" @change="toggleZone(z)" /><span class="mp-swatch" :style="{ background: z.color }"></span>{{ z.label }}</label>
      <div v-if="!geofences.length" class="mp-empty">No geofences.</div>
    </div>

    <!-- Layers panel — saved places toggle + per-layer (map_layer/list) rectangles, searchable -->
    <div v-if="panel==='layers'" class="map-panel">
      <div class="mp-head"><i class="material-icons">layers</i><span>Layers</span><button class="mp-x" v-tip="'Close'" @click="panel=null"><i class="material-icons">close</i></button></div>
      <input class="mp-search" v-model="layerQ" type="text" placeholder="Search layers" />
      <label v-if="places.length" class="mp-check"><input type="checkbox" :checked="poiVisible" @change="setPoiVisible(!poiVisible)" /> Saved places ({{ places.length }})</label>
      <label v-for="l in filteredLayers" :key="l.id" class="mp-check"><input type="checkbox" :checked="!!activeLayers[l.id]" @change="toggleLayer(l)" /> {{ l.title }}</label>
      <div v-if="!filteredLayers.length" class="mp-empty">No layers.</div>
    </div>

    <!-- Search panel — local object search + Nominatim address geocoding -->
    <div v-if="panel==='search'" class="map-panel">
      <div class="mp-head"><i class="material-icons">search</i><span>Find</span><button class="mp-x" v-tip="'Close'" @click="panel=null"><i class="material-icons">close</i></button></div>
      <input class="mp-search" v-model="searchQ" type="text" placeholder="Object or address" @input="onSearchInput" @keyup.enter="geocode" />
      <div v-if="objectHits.length" class="mp-sect">Objects</div>
      <div v-for="t in objectHits" :key="'o'+t.id" class="mp-hit" @click="pickObject(t)"><i class="material-icons hit-ico">place</i>{{ t.label || ('#'+t.id) }}</div>
      <div v-if="addressHits.length" class="mp-sect">Addresses</div>
      <div v-for="(a,i) in addressHits" :key="'a'+i" class="mp-hit" @click="pickAddress(a)"><i class="material-icons hit-ico">location_city</i>{{ a.display_name }}</div>
      <div v-if="!objectHits.length && !addressHits.length" class="mp-empty">{{ geoLoading ? 'Searching…' : 'No object — press Enter for address.' }}</div>
    </div>

    <!-- Favourites panel — saved camera views (localStorage) -->
    <div v-if="panel==='fav'" class="map-panel">
      <div class="mp-head"><i class="material-icons">star</i><span>Saved views</span><button class="mp-x" v-tip="'Close'" @click="panel=null"><i class="material-icons">close</i></button></div>
      <button class="mp-btn" @click="saveView"><i class="material-icons">add_location_alt</i>Save current view</button>
      <div v-for="(v,i) in favViews" :key="i" class="mp-hit fav-row">
        <span class="fav-name" @click="gotoView(v)"><i class="material-icons hit-ico">star</i>{{ v.name }}</span>
        <button class="fav-del" v-tip="'Remove'" @click="delView(i)"><i class="material-icons">close</i></button>
      </div>
      <div v-if="!favViews.length" class="mp-empty">No saved views yet.</div>
    </div>

    <div v-if="loadError" class="lmap-err">Map failed to load (VITE_GMAPS_KEY?).</div>
  </div>
</template>

<script>
import { loadLeafletStack } from './leaflet-stack.js'
import { tripColor } from '../shell/track-trips.js'

const STATUS_COLORS = { moving: '#2e9e4b', stopped: '#f2960f', parked: '#f2960f', blocked: '#e53935', idle: '#9aa7b2' }
// Speed → hotline gradient (0..60 km/h): red (stopped) → orange → amber → light-green → green.
// Speed → hotline gradient keyed on normalised speed (0=slow … 1=fast). Matches the real
// go.lacak.io palette {0:green, .5:yellow, 1:red} — slow is green, fast is red.
const SPEED_PALETTE = { 0.0: '#00c853', 0.5: '#ffd600', 1.0: '#e53935' }
const MARKER_PALETTE = ['#1E96DC', '#E91E63', '#8E44AD', '#16A085', '#E67E22', '#2E9E4B', '#F2960F', '#3F51B5', '#00838F', '#90A4AE']
const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
function esc(v) { return String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ESC_MAP[c]) }
// Whitelist a colour before it is concatenated into an SVG/HTML divIcon string. Group + saved-place
// colours come from the backend (tracker/group/list, place/list), so a hostile value could break
// out of a fill="…" attribute and inject markup when Leaflet assigns divIcon innerHTML. Accept only
// #hex / rgb()/rgba() / hsl()/hsla() / plain css colour words; anything else → neutral grey.
function safeColor(c) {
  const s = String(c == null ? '' : c).trim()
  return /^#[0-9a-fA-F]{3,8}$/.test(s) || /^(rgb|rgba|hsl|hsla)\([0-9.,%\s/]+\)$/.test(s) || /^[a-zA-Z]{1,20}$/.test(s) ? s : '#90a4ae'
}

// --- spherical geometry (ports the Google geometry.spherical bits used by the map tools) ---
const EARTH_R = 6378137 // metres
// Destination point `distM` metres from (lat,lng) along `bearingDeg`.
function destPoint(lat, lng, distM, bearingDeg) {
  const d = distM / EARTH_R
  const brng = (bearingDeg * Math.PI) / 180
  const la1 = (lat * Math.PI) / 180
  const ln1 = (lng * Math.PI) / 180
  const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(brng))
  const ln2 = ln1 + Math.atan2(Math.sin(brng) * Math.sin(d) * Math.cos(la1), Math.cos(d) - Math.sin(la1) * Math.sin(la2))
  return [(la2 * 180) / Math.PI, (((ln2 * 180) / Math.PI + 540) % 360) - 180]
}
// Initial bearing (deg) from a to b.
function bearingOf(a, b) {
  const la1 = (a.lat * Math.PI) / 180
  const la2 = (b.lat * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const y = Math.sin(dLng) * Math.cos(la2)
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLng)
  return (((Math.atan2(y, x) * 180) / Math.PI) + 360) % 360
}
// Great-circle length (metres) of a [{lat,lng}] path.
function pathLength(pts) {
  let m = 0
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]; const b = pts[i]
    const la1 = (a.lat * Math.PI) / 180; const la2 = (b.lat * Math.PI) / 180
    const dLa = la2 - la1; const dLn = ((b.lng - a.lng) * Math.PI) / 180
    const h = Math.sin(dLa / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLn / 2) ** 2
    m += 2 * EARTH_R * Math.asin(Math.min(1, Math.sqrt(h)))
  }
  return m
}
// Buffer a centre-line into a "sausage" polygon ring, ±radius metres perpendicular at each vertex.
function corridorPolygon(path, radius) {
  const left = []; const right = []
  for (let i = 0; i < path.length; i++) {
    const prev = path[i - 1] || path[i]
    const next = path[i + 1] || path[i]
    const hdg = bearingOf(prev, next)
    left.push(destPoint(path[i].lat, path[i].lng, radius, hdg - 90))
    right.push(destPoint(path[i].lat, path[i].lng, radius, hdg + 90))
  }
  return left.concat(right.reverse())
}
const VEHICLE_FAMILY = {
  car: 'car', taxi: 'car', ev: 'car', pickup: 'van', van: 'van', rv: 'van',
  truck: 'truck', firetruck: 'truck', tractor: 'truck', lorry: 'truck', dump: 'truck',
  bus: 'bus', moto: 'moto', bike: 'moto', boat: 'boat'
}
// Google mapTypeId per basemap key (GoogleMutant `type`); bing_* handled by a tile layer.
const BASE_DEFS = [
  { key: 'google', label: 'Google', gm: 'roadmap' },
  { key: 'bing', label: 'Bing', bing: 'road' },
  { key: 'google_satellite', label: 'Google satellite', gm: 'satellite' },
  { key: 'bing_satellite', label: 'Bing satellite', bing: 'aerial' },
  { key: 'bing_hybrid', label: 'Bing hybrid', bing: 'hybrid' },
  { key: 'google_hybrid', label: 'Google hybrid', gm: 'hybrid' }
]

export default {
  name: 'TrackerMapLeaflet',
  props: {
    trackers: { type: Array, default: () => [] },
    states: { type: Object, default: () => ({}) },
    activeId: { type: [Number, String], default: null },
    mapLayers: { type: Array, default: () => [] },
    places: { type: Array, default: () => [] },
    geofences: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    display: { type: Object, default: () => ({ labels: true, tails: true, cluster: true, animation: true, follow: true, showOnlySelected: false, hideOffline: false, showInfoByClick: false, selectedIds: {} }) },
    iconOverrides: { type: Object, default: () => ({}) }
  },
  data() {
    return { L: null, map: null, base: 'google', baseLayers: BASE_DEFS, panel: null, loadError: false, clustered: true, overlays: { traffic: false, transit: false, bike: false }, followId: null,
      // Stage 2: overlay toggles (geofences + custom map-layers) and saved-places visibility.
      activeZones: {}, activeLayers: {}, poiVisible: true, layerQ: '',
      // Stage 2: interaction state — mode tools (poi/measure/route) + reference-point pick.
      mode: null, pickingRef: false,
      // Stage 2 tools: search (objects + Nominatim), measure, route (OSRM), favourites, add-place.
      searchQ: '', objectHits: [], addressHits: [], geoLoading: false,
      measureText: '0 m', routePts: [], routeText: '', favViews: [], poiForm: null,
      // Places dropped via "Add place" — rendered through the managed _poiLayer, not loose markers.
      localPlaces: [] }
  },
  computed: {
    // Case-insensitive filter of the custom map-layers by title (matches the Google engine).
    filteredLayers() {
      const q = (this.layerQ || '').trim().toLowerCase()
      if (!q) return this.mapLayers
      return this.mapLayers.filter((l) => String(l.title || '').toLowerCase().includes(q))
    },
    groupColorById() {
      const m = {}
      this.groups.forEach((g) => { if (g && g.color != null) m[g.id] = g.color[0] === '#' ? g.color : '#' + g.color })
      return m
    }
  },
  async mounted() {
    try {
      const L = await loadLeafletStack()
      this.L = L
      const gmaps = window.google.maps
      this.map = L.map(this.$refs.map, { center: [-6.2, 106.8], zoom: 11, zoomControl: false, attributionControl: false })
      // Single map-click router for the interaction tools (reference pick / measure / poi / route).
      this.map.on('click', (e) => this.onMapClick(e.latlng))
      // Dragging the map cancels "follow" (matches the Google engine) — otherwise the next
      // telemetry tick would snap the camera back to the followed tracker and the user could
      // never pan away.
      this.map.on('dragstart', () => { this.followId = null })
      // GoogleMutant = Google tiles inside Leaflet (the real basemap engine).
      this.baseLayer = L.gridLayer.googleMutant({ type: 'roadmap' })
      this.baseLayer.addTo(this.map)
      // PruneCluster (canvas-style donut) for clustering.
      this.pc = new L.__PruneClusterForLeaflet(120)
      this.pc.PrepareLeafletMarker = (marker, data) => {
        marker.setIcon(this.markerDivIcon(data.tracker, data.status))
        data._iconKey = this.iconKey(data.tracker, data.status) // track rendered state for the diff in sync()
        marker.off('click')
        marker.on('click', () => this.$emit('marker-click', data.tracker))
      }
      this.pc.BuildLeafletClusterIcon = (cluster) => this.clusterDivIcon(cluster)
      this.map.addLayer(this.pc)
      this._gmaps = gmaps
      // Stage-2 overlays: geofence + custom-layer shapes are added on toggle (keyed by id);
      // saved places live in their own group, visible by default like the Google engine.
      this._zoneLayers = {}
      this._layerLayers = {}
      this._poiLayer = L.layerGroup().addTo(this.map)
      // History playback state (Stage 7): track layers + the moving arrow marker. Non-reactive
      // instance props (Leaflet layers don't belong in Vue's reactive data).
      this._histLayers = []
      this.histPts = []
      this.histMarker = null
      this.renderPlaces()
      try { this.favViews = JSON.parse(localStorage.getItem('tracko-fav-views') || '[]') || [] } catch (e) { this.favViews = [] }
      this.sync()
    } catch (e) {
      this.loadError = true
      // eslint-disable-next-line no-console
      console.warn('[tracko] Leaflet map load failed:', e && e.message)
    }
  },
  beforeDestroy() { if (this.map) this.map.remove() },
  watch: {
    trackers: { handler() { this.sync() }, deep: true },
    states: { handler() { this.sync() }, deep: true },
    // re-render markers when the selection changes so the selected one gets the backlight veil
    activeId() { this.sync() },
    display: { handler() { this.sync(); this.renderPlaces() }, deep: true },
    places: { handler() { this.renderPlaces() }, deep: true }
  },
  methods: {
    // ---- state helpers (same semantics as the Google map) ----
    statusOf(t) {
      if (t.source && t.source.blocked) return 'blocked'
      const s = this.states[t.id]
      if (!s || typeof s !== 'object') return 'idle'
      const ms = s.movement_status
      return ms === 'moving' ? 'moving' : ms === 'stopped' ? 'stopped' : 'parked'
    },
    latlngOf(t) {
      const s = this.states[t.id]
      const loc = s && s.gps && s.gps.location
      return loc && typeof loc.lat === 'number' ? { lat: loc.lat, lng: loc.lng } : null
    },
    headingOf(t) { const s = this.states[t.id]; return (s && s.gps && s.gps.heading) || 0 },
    objectColor(t) {
      const ov = this.iconOverrides[t.id]
      if (ov && ov.color) return safeColor(ov.color)
      const gc = t && this.groupColorById[t.group_id != null ? t.group_id : 0]
      if (gc) return safeColor(gc)
      const digits = String(t.id).replace(/\D/g, '')
      const n = digits ? parseInt(digits.slice(-3), 10) : 0
      return MARKER_PALETTE[n % MARKER_PALETTE.length]
    },
    // ---- marker + cluster icons (SVG in an L.divIcon) ----
    markerShape(status, color, heading) {
      if (status === 'moving') return '<polygon points="16,26.98 6.32,31 16,1.8 25.68,31 16,26.98" stroke="' + color + '" stroke-width="1.5" fill="' + color + '" transform="rotate(' + (Number(heading) || 0) + ' 16 16)"/>'
      if (status === 'stopped') return '<rect x="5" y="5" width="22" height="22" fill="' + color + '"/>'
      return '<circle cx="16" cy="16" r="14" fill="' + color + '"/><text x="16" y="17" font-size="16" font-family="sans-serif" fill="#FFFFFF" text-anchor="middle" dominant-baseline="central">P</text>'
    },
    vehicleShape(iconKey, color, heading) {
      const fam = VEHICLE_FAMILY[iconKey] || 'car'
      const fill = ' fill="' + color + '" stroke="#ffffff" stroke-width="1"'
      const glass = ' fill="#ffffff" fill-opacity="0.55" stroke="none"'
      let body
      if (fam === 'van') body = '<rect x="9.5" y="3.5" width="13" height="25" rx="2.6"' + fill + '/><rect x="11" y="5.6" width="10" height="4.4" rx="1"' + glass + '/>'
      else if (fam === 'truck') body = '<rect x="10.5" y="3" width="11" height="8" rx="2"' + fill + '/><rect x="11" y="12" width="10" height="17" rx="1.2"' + fill + '/>'
      else if (fam === 'bus') body = '<rect x="10.5" y="2.5" width="11" height="27" rx="4.5"' + fill + '/><rect x="12" y="5" width="8" height="4" rx="1"' + glass + '/>'
      else if (fam === 'moto') body = '<rect x="13" y="6" width="6" height="20" rx="3"' + fill + '/><circle cx="16" cy="9" r="2.6"' + glass + '/>'
      else if (fam === 'boat') body = '<path d="M16 2 L22 13 L20.5 29 L11.5 29 L10 13 Z"' + fill + '/>'
      else body = '<rect x="10" y="4.5" width="12" height="23" rx="4.5"' + fill + '/><rect x="11.5" y="8.6" width="9" height="6" rx="1.6"' + glass + '/>'
      return '<g transform="rotate(' + (Number(heading) || 0) + ' 16 16)">' + body + '</g>'
    },
    markerDivIcon(t, status) {
      const L = this.L
      const color = this.objectColor(t)
      const selected = String(this.activeId) === String(t.id)
      const ring = selected ? (status === 'stopped'
        ? '<rect x="3.4" y="3.4" width="25.2" height="25.2" fill="none" stroke="#1E96DC" stroke-width="1.8"/>'
        : '<circle cx="16" cy="16" r="15.1" fill="none" stroke="#1E96DC" stroke-width="1.8"/>') : ''
      const name = this.display.labels ? (t.label || ('#' + t.id)) : ''
      const lw = name ? Math.max(10, Math.round(name.length * 6.4) + 16) : 0
      const W = name ? 36 + lw : 32
      const label = name
        ? '<g filter="url(#lsh)"><rect x="36" y="6" width="' + lw + '" height="20" fill="' + color + '"/><text x="44" y="17" font-family="Roboto,Arial,sans-serif" font-size="11" font-weight="700" fill="#FFFFFF" dominant-baseline="central">' + esc(name) + '</text></g>'
        : ''
      const ov = this.iconOverrides[t.id]
      const shape = (ov && ov.icon) ? this.vehicleShape(ov.icon, color, this.headingOf(t)) : this.markerShape(status, color, this.headingOf(t))
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="32" viewBox="0 0 ' + W + ' 32">' +
        '<defs><filter id="msh" x="-80%" y="-80%" width="260%" height="260%"><feDropShadow dx="0" dy="0" stdDeviation="1.2" flood-color="#000000" flood-opacity="0.4"/></filter>' +
        '<filter id="lsh" x="-8%" y="-70%" width="120%" height="240%"><feDropShadow dx="0" dy="0" stdDeviation="1.1" flood-color="#000000" flood-opacity="0.2"/></filter></defs>' +
        '<g filter="url(#msh)">' + ring + shape + '</g>' + label + '</svg>'
      // Selected marker gets the real go.lacak.io "backlight" — a pulsing translucent-blue
      // veil behind the icon (the reference-point veil recipe: 64px, rgba(30,150,220,.125),
      // enlarge 0.4→1 scale, 1.2s infinite). It sits behind the shape at the marker centre.
      const veil = selected ? '<div class="lmarker-veil"></div>' : ''
      const html = '<div class="lmarker-wrap">' + veil + svg + '</div>'
      return L.divIcon({ html, className: 'lmarker', iconSize: [W, 32], iconAnchor: [16, 16] })
    },
    clusterDivIcon(cluster) {
      const L = this.L
      const count = (cluster && cluster.population) || 0
      // tally colours across the cluster's markers (transparent group-donut, like tt-cluster).
      // PruneCluster doesn't always expose GetClusterMarkers here — guard everything and fall
      // back to a single grey ring so a cluster icon can never throw.
      const tally = {}
      let markers = []
      try { if (cluster && typeof cluster.GetClusterMarkers === 'function') markers = cluster.GetClusterMarkers() || [] } catch (e) { markers = [] }
      if (Array.isArray(markers)) markers.forEach((m) => { const c = (m && m.data && m.data.color) || '#90A4AE'; tally[c] = (tally[c] || 0) + 1 })
      if (!Object.keys(tally).length) tally['#90A4AE'] = count || 1
      const size = count < 100 ? 48 : count < 1000 ? 56 : 64
      const sw = Math.round(size / 4)
      const cx = size / 2
      const r = cx - sw / 2 - 1
      const circ = 2 * Math.PI * r
      let offset = 0; let segs = ''
      const total = Object.keys(tally).reduce((a, k) => a + tally[k], 0) || count
      Object.keys(tally).forEach((c) => {
        const dash = (tally[c] / total) * circ
        segs += '<circle cx="' + cx + '" cy="' + cx + '" r="' + r + '" fill="none" stroke="' + c + '" stroke-width="' + sw + '" stroke-dasharray="' + dash.toFixed(2) + ' ' + (circ - dash).toFixed(2) + '" stroke-dashoffset="' + (-offset).toFixed(2) + '" transform="rotate(-90 ' + cx + ' ' + cx + ')"/>'
        offset += dash
      })
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '" viewBox="0 0 ' + size + ' ' + size + '">' + segs +
        '<text x="' + cx + '" y="' + (cx + 1) + '" text-anchor="middle" dominant-baseline="central" font-family="Roboto,Arial,sans-serif" font-size="' + Math.round(size * 0.3) + '" font-weight="700" fill="#263238" stroke="#ffffff" stroke-width="0.6" paint-order="stroke">' + count + '</text></svg>'
      return L.divIcon({ html: svg, className: 'tt-cluster', iconSize: [size, size], iconAnchor: [cx, cx] })
    },
    // A stable key for a marker's VISIBLE state — only rebuild the divIcon when it changes.
    iconKey(t, status) {
      const sel = String(this.activeId) === String(t.id) ? 1 : 0
      const ov = this.iconOverrides[t.id]
      return status + '|' + this.objectColor(t) + '|' + this.headingOf(t) + '|' + sel + '|' + (this.display.labels ? 1 : 0) + '|' + ((ov && ov.icon) || '')
    },
    // ---- feed markers into PruneCluster (INCREMENTAL — reuse markers across polls) ----
    sync() {
      const L = this.L
      if (!L || !this.pc) return
      if (!this._pcMarkers) this._pcMarkers = {}
      if (!this._tails) this._tails = {}
      const d = this.display || {}
      const onlySel = !!d.showOnlySelected
      const sel = d.selectedIds || {}
      const desired = {}
      this.trackers.forEach((t) => {
        if (d.hideOffline && this.statusOf(t) === 'idle') return
        // "Show only selected objects" — hide everything not ticked in the Objects panel.
        if (onlySel && !sel[t.id]) return
        const ll = this.latlngOf(t); if (!ll) return
        desired[t.id] = true
        const status = this.statusOf(t)
        let m = this._pcMarkers[t.id]
        if (!m) {
          m = new L.__PruneCluster.Marker(ll.lat, ll.lng, { tracker: t, status, color: this.objectColor(t), id: t.id })
          this._pcMarkers[t.id] = m
          this.pc.RegisterMarker(m)
        } else {
          m.Move(ll.lat, ll.lng)                       // update position in place (no realloc)
          m.data.tracker = t; m.data.status = status; m.data.color = this.objectColor(t)
          // Repaint the rendered icon only when its visible state actually changed
          // (status/heading/colour/selection/label/override). PruneCluster keeps the live
          // leaflet marker on its INTERNAL cluster, never on m.data, so we cannot setIcon it
          // directly — instead flag forceIconRedraw and let the ProcessView() below re-run
          // PrepareLeafletMarker, which repaints via markerDivIcon() and refreshes _iconKey,
          // then self-clears the flag (PruneCluster.js:629-632). We leave _iconKey untouched
          // here so the flag re-arms every sync until a real repaint lands it — self-healing
          // even while the marker is hidden inside a cluster.
          if (m.data._iconKey !== this.iconKey(t, status)) m.data.forceIconRedraw = true
        }
        // Tail / "Trace" — the fading breadcrumb polyline of recent fixes (display.tails).
        const s = this.states[t.id]
        const tail = (d.tails && s && Array.isArray(s.tail)) ? s.tail.filter((p) => p && typeof p.lat === 'number').map((p) => [p.lat, p.lng]) : []
        if (tail.length > 1) {
          if (this._tails[t.id]) this._tails[t.id].setLatLngs(tail)
          else this._tails[t.id] = L.polyline(tail, { color: STATUS_COLORS[status] || '#1e88c7', weight: 3, opacity: 0.55, interactive: false }).addTo(this.map)
        } else if (this._tails[t.id]) { this.map.removeLayer(this._tails[t.id]); delete this._tails[t.id] }
      })
      // drop the markers + tails that left the roster or got filtered out
      Object.keys(this._pcMarkers).forEach((id) => {
        if (!desired[id]) { this.pc.RemoveMarkers([this._pcMarkers[id]]); delete this._pcMarkers[id] }
      })
      Object.keys(this._tails).forEach((id) => {
        if (!desired[id]) { this.map.removeLayer(this._tails[id]); delete this._tails[id] }
      })
      this.pc.ProcessView()
      if (this.followId != null) this.recenterActive()
    },
    // ---- basemap ----
    overlayName(key) { return key === 'traffic' ? 'TrafficLayer' : key === 'transit' ? 'TransitLayer' : 'BicyclingLayer' },
    setBase(key) {
      const L = this.L; if (!L || !this.map) return
      const def = BASE_DEFS.find((b) => b.key === key) || BASE_DEFS[0]
      this.base = key
      if (this.baseLayer) { this.map.removeLayer(this.baseLayer); this.baseLayer = null }
      if (def.bing) {
        this.baseLayer = L.tileLayer(this.bingTileUrl(def.bing), { maxZoom: 19, subdomains: def.bing === 'road' ? 'abcd' : 'abc' })
      } else {
        this.baseLayer = L.gridLayer.googleMutant({ type: def.gm })
      }
      this.baseLayer.addTo(this.map)
      // re-attach any active Google overlays onto the NEW base mutant (they belong to the base,
      // not a separate layer) so they don't get orphaned / repaint an opaque roadmap.
      if (typeof this.baseLayer.addGoogleLayer === 'function') {
        Object.keys(this.overlays).forEach((k) => { if (this.overlays[k]) this.baseLayer.addGoogleLayer(this.overlayName(k)) })
      }
    },
    bingTileUrl(kind) {
      // keyless fallbacks (Esri imagery / CARTO roads) unless a real Bing key is present.
      const key = import.meta.env.VITE_BING_KEY
      if (key) return 'https://ecn.t{s}.tiles.virtualearth.net/tiles/' + (kind === 'aerial' ? 'a' : kind === 'hybrid' ? 'h' : 'r') + '{q}.jpeg?g=1&key=' + key // (quadkey via a custom layer needed for real Bing)
      if (kind === 'aerial' || kind === 'hybrid') return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      return 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png'
    },
    toggleOverlay(key) {
      const L = this.L; if (!L || !this.map) return
      const on = !this.overlays[key]
      this.overlays = Object.assign({}, this.overlays, { [key]: on })
      // Attach the Google overlay onto the ACTIVE base mutant so it never repaints an opaque
      // roadmap over a satellite/Bing base. (On a Bing tile base there is no mutant to attach
      // to, so the overlay is a no-op there — a documented limitation, not a broken render.)
      const base = this.baseLayer
      if (base && typeof base.addGoogleLayer === 'function') {
        if (on) base.addGoogleLayer(this.overlayName(key))
        else if (typeof base.removeGoogleLayer === 'function') base.removeGoogleLayer(this.overlayName(key))
      }
    },
    // ---- parent-contract methods (Stage 1: markers/cluster/basemap; rest arrive next stages) ----
    trackerById(id) { return this.trackers.find((t) => String(t.id) === String(id)) || null },
    // Path A (row-body click): one-shot fit to the tracker — no follow, no widgets. fitBounds on
    // the single point centres + zooms in (capped so it doesn't slam to street level).
    softSelect(id) {
      this.followId = null
      const L = this.L; const t = this.trackerById(id); const ll = t && this.latlngOf(t)
      if (ll && L && this.map) this.map.fitBounds(L.latLngBounds([[ll.lat, ll.lng]]), { maxZoom: 15, padding: [40, 40] })
    },
    focus(id) { const t = this.trackerById(id); const ll = t && this.latlngOf(t); if (ll && this.map) { this.followId = id; this.map.setView([ll.lat, ll.lng], 15) } },
    recenterActive() { const t = this.followId != null && this.trackerById(this.followId); const ll = t && this.latlngOf(t); if (ll && this.map) this.map.panTo([ll.lat, ll.lng]) },
    fitAll() {
      const L = this.L; const pts = this.trackers.map((t) => this.latlngOf(t)).filter(Boolean)
      if (pts.length && this.map) this.map.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])), { padding: [60, 60] })
    },
    // Styled zoom (Leaflet's own zoomControl is off so these carry the app look + tooltips).
    zoomBy(d) { if (this.map) this.map.setZoom(this.map.getZoom() + d) },
    // Fullscreen the whole map root; the v-tip node re-homes into it so tooltips stay visible.
    toggleFullscreen() {
      const el = this.$el
      if (!el) return
      if (document.fullscreenElement) { if (document.exitFullscreen) document.exitFullscreen() }
      else if (el.requestFullscreen) { el.requestFullscreen() }
    },
    // ---- Stage 2: side panels + overlays ----
    togglePanel(p) { this.pickingRef = false; this.panel = this.panel === p ? null : p },
    // Single map-click router — precedence mirrors the Google engine's onMapClick.
    onMapClick(latlng) {
      const ll = { lat: latlng.lat, lng: latlng.lng }
      if (this.pickingRef) { this.setReferencePoint(ll); this.pickingRef = false; this.$emit('reference-set', ll); return }
      if (this.mode === 'measure') { this.addMeasurePoint(ll); return }
      if (this.mode === 'poi') { this.dropPoi(ll); return }
      if (this.mode === 'route') { this.addRoutePoint(ll) }
    },
    // Mode tools — toggle, close panel, cancel pick, tear down the other tools on leave.
    setMode(m) {
      this.pickingRef = false
      this.mode = this.mode === m ? null : m
      this.panel = null
      if (this.mode !== 'measure') this.resetMeasure()
      if (this.mode !== 'route') this.resetRoute()
      if (this.mode !== 'poi') this.poiForm = null
    },
    // --- search: local objects + Nominatim addresses ---
    onSearchInput() {
      const q = (this.searchQ || '').trim().toLowerCase()
      this.objectHits = q ? this.trackers.filter((t) => String(t.label || '').toLowerCase().includes(q)).slice(0, 6) : []
      if (!q) this.addressHits = []
    },
    pickObject(t) { this.panel = null; this.$emit('marker-click', t); this.focus(t.id) },
    async geocode() {
      const q = (this.searchQ || '').trim()
      if (q.length < 3) return
      this.geoLoading = true
      try {
        const res = await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=5&q=' + encodeURIComponent(q))
        this.addressHits = (await res.json()) || []
      } catch (e) { this.addressHits = [] } finally { this.geoLoading = false }
    },
    pickAddress(a) {
      this.panel = null
      const lat = parseFloat(a.lat); const lng = parseFloat(a.lon)
      if (!this.map) return
      this.map.setView([lat, lng], 16)
      if (this._searchMarker) this._searchMarker.setLatLng([lat, lng])
      else this._searchMarker = this.L.marker([lat, lng]).addTo(this.map)
      this._searchMarker.bindPopup('<div class="tracko-pop">' + esc(a.display_name) + '</div>').openPopup()
    },
    // --- measure: click points → polyline + running distance ---
    addMeasurePoint(ll) {
      const L = this.L
      this._measurePts = this._measurePts || []
      this._measurePts.push([ll.lat, ll.lng])
      if (this._measureLine) this._measureLine.setLatLngs(this._measurePts)
      else this._measureLine = L.polyline(this._measurePts, { color: '#1976d2', weight: 3 }).addTo(this.map)
      const total = pathLength(this._measurePts.map((p) => ({ lat: p[0], lng: p[1] })))
      this.measureText = total >= 1000 ? (total / 1000).toFixed(2) + ' km' : Math.round(total) + ' m'
    },
    resetMeasure() {
      if (this._measureLine && this.map) { this.map.removeLayer(this._measureLine); this._measureLine = null }
      this._measurePts = []; this.measureText = '0 m'
    },
    // --- route: two points → OSRM driving polyline ---
    addRoutePoint(ll) {
      const L = this.L
      if (this.routePts.length >= 2) this.resetRoute()
      this.routePts.push(ll)
      this._routeMarkers = this._routeMarkers || []
      this._routeMarkers.push(L.circleMarker([ll.lat, ll.lng], { radius: 5, color: '#fff', weight: 2, fillColor: '#1565c0', fillOpacity: 1 }).addTo(this.map))
      if (this.routePts.length === 2) this.buildRoute()
    },
    async buildRoute() {
      const a = this.routePts[0]; const b = this.routePts[1]
      const gen = (this._routeGen || 0) // generation token — a reset (3rd click / leave mode) bumps it
      try {
        const url = 'https://router.project-osrm.org/route/v1/driving/' + a.lng + ',' + a.lat + ';' + b.lng + ',' + b.lat + '?overview=full&geometries=geojson'
        const res = await fetch(url); const data = await res.json()
        // A reset landed while OSRM was in flight — drop this stale response so it can't paint a
        // markerless phantom route for points the user already cleared.
        if (gen !== (this._routeGen || 0) || this.routePts.length !== 2) return
        const r = data && data.routes && data.routes[0]
        if (!r) { this.routeText = 'no route'; return }
        const latlngs = r.geometry.coordinates.map((c) => [c[1], c[0]])
        if (this._routeLine && this.map) this.map.removeLayer(this._routeLine)
        this._routeLine = this.L.polyline(latlngs, { color: '#1565c0', weight: 5, opacity: 0.8 }).addTo(this.map)
        this.routeText = (r.distance / 1000).toFixed(1) + ' km · ' + Math.round(r.duration / 60) + ' min'
      } catch (e) { if (gen === (this._routeGen || 0)) this.routeText = 'route error' }
    },
    resetRoute() {
      this._routeGen = (this._routeGen || 0) + 1 // supersede any in-flight buildRoute
      if (this._routeLine && this.map) { this.map.removeLayer(this._routeLine); this._routeLine = null }
      if (this._routeMarkers) { this._routeMarkers.forEach((m) => { if (this.map) this.map.removeLayer(m) }); this._routeMarkers = [] }
      this.routePts = []; this.routeText = ''
    },
    // --- favourites: saved camera views (localStorage 'tracko-fav-views') ---
    saveView() {
      if (!this.map) return
      const c = this.map.getCenter(); const z = this.map.getZoom()
      this.favViews.push({ name: 'View ' + (this.favViews.length + 1) + ' (z' + z + ')', lat: c.lat, lng: c.lng, z })
      this.persistViews()
    },
    gotoView(v) { this.panel = null; if (this.map) this.map.setView([v.lat, v.lng], v.z) },
    delView(i) { this.favViews.splice(i, 1); this.persistViews() },
    persistViews() { try { localStorage.setItem('tracko-fav-views', JSON.stringify(this.favViews)) } catch (e) { /* ignore quota */ } },
    // --- add-place: click → form → place/create ---
    dropPoi(ll) { this.poiForm = { lat: ll.lat, lng: ll.lng, name: '' } },
    async savePoi() {
      const f = this.poiForm; if (!f) return
      const name = (f.name || '').trim() || 'New place'
      // Render the new place through the managed _poiLayer (via localPlaces) instead of a loose
      // map marker — so renderPlaces owns it (no unbounded accumulation, no double with the
      // teardrop pin the parent's places refresh would draw).
      this.localPlaces.push({ id: 'new-' + this.localPlaces.length, label: name, location: { lat: f.lat, lng: f.lng }, color: '#d81b60' })
      this.renderPlaces()
      try { const { request } = await import('@/api/request'); await request('place/create', { place: { label: name, location: { lat: f.lat, lng: f.lng } } }) } catch (e) { /* demo backend may 404 */ }
      this.poiForm = null; this.setMode(null)
    },
    cancelPoi() { this.poiForm = null; this.setMode(null) },
    // Reference-point pick (invoked by the Objects panel "sort by distance").
    startPickReference() { this.pickingRef = true; this.mode = null; this.panel = null },
    setReferencePoint(ll) {
      const L = this.L; if (!L || !this.map) return
      const html = '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">' +
        '<path d="M15 1 C7 1 1 7 1 15 C1 26 15 39 15 39 C15 39 29 26 29 15 C29 7 23 1 15 1 Z" fill="#1e88c7" stroke="#fff" stroke-width="2"/>' +
        '<circle cx="15" cy="14" r="5.5" fill="#fff"/><circle cx="15" cy="14" r="2.6" fill="#1e88c7"/></svg>'
      const icon = L.divIcon({ html, className: 'lref', iconSize: [30, 40], iconAnchor: [15, 39] })
      if (this._refMarker) this._refMarker.setLatLng([ll.lat, ll.lng])
      else this._refMarker = L.marker([ll.lat, ll.lng], { icon, zIndexOffset: 2500 }).addTo(this.map)
    },
    clearReferencePoint() {
      this.pickingRef = false
      if (this._refMarker && this.map) { this.map.removeLayer(this._refMarker); this._refMarker = null }
    },
    // Saved places (place/list): teardrop pin + optional radius circle + click popup.
    renderPlaces() {
      const L = this.L
      if (!L || !this._poiLayer) return
      this._poiLayer.clearLayers()
      if (!this.poiVisible) return
      ;(this.places || []).concat(this.localPlaces || []).forEach((p) => {
        const loc = p.location || p
        if (loc == null || typeof loc.lat !== 'number') return
        const color = safeColor(p.color || '#5c6bc0') // backend-sourced → validate before it hits divIcon HTML
        const label = p.label || 'Place'
        if (p.radius) L.circle([loc.lat, loc.lng], { radius: p.radius, color, weight: 1, fillColor: color, fillOpacity: 0.12, interactive: false }).addTo(this._poiLayer)
        const html = '<svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">' +
          '<path d="M13 1 C6 1 1 6 1 13 C1 22 13 33 13 33 C13 33 25 22 25 13 C25 6 20 1 13 1 Z" fill="' + color + '" stroke="#fff" stroke-width="2"/>' +
          '<circle cx="13" cy="11" r="4" fill="#fff"/></svg>' +
          (this.display.labels ? '<div class="lpoi-label">' + esc(label) + '</div>' : '')
        const icon = L.divIcon({ html, className: 'lpoi', iconSize: [26, 34], iconAnchor: [13, 33] })
        const m = L.marker([loc.lat, loc.lng], { icon, zIndexOffset: 400 }).addTo(this._poiLayer)
        const addr = loc.address ? '<div>' + esc(loc.address) + '</div>' : ''
        m.bindPopup('<div class="tracko-pop"><strong>' + esc(label) + '</strong>' + addr + '</div>')
      })
    },
    setPoiVisible(on) { this.poiVisible = !!on; this.renderPlaces() },
    // Geofences (zone/list): circle / corridor(buffered sausage) / polygon — toggle add/remove.
    toggleZone(z) {
      const L = this.L; if (!L || !this.map) return
      if (this._zoneLayers[z.id]) {
        this.map.removeLayer(this._zoneLayers[z.id]); delete this._zoneLayers[z.id]
        this.$delete(this.activeZones, z.id); return
      }
      const st = { color: z.color, weight: 2, fillColor: z.color, fillOpacity: 0.15, interactive: false }
      let layer
      if (z.type === 'corridor') layer = L.polygon(corridorPolygon(z.path || z.points || [], z.radius || 200), st)
      else if (z.type === 'polygon') layer = L.polygon((z.points || []).map((p) => [p.lat, p.lng]), st)
      else layer = L.circle([z.center.lat, z.center.lng], { radius: z.radius, color: z.color, weight: 2, fillColor: z.color, fillOpacity: 0.15, interactive: false })
      layer.addTo(this.map)
      this._zoneLayers[z.id] = layer
      this.$set(this.activeZones, z.id, true)
    },
    // Custom map-layers (map_layer/list): a bounding-box rectangle from l.bounds ([lat,lng] pairs).
    toggleLayer(l) {
      const L = this.L; if (!L || !this.map) return
      if (this._layerLayers[l.id]) {
        this.map.removeLayer(this._layerLayers[l.id]); delete this._layerLayers[l.id]
        this.$delete(this.activeLayers, l.id); return
      }
      if (!Array.isArray(l.bounds) || !l.bounds.length) return
      const lats = l.bounds.map((b) => b[0]); const lngs = l.bounds.map((b) => b[1])
      const rect = L.rectangle([[Math.min.apply(null, lats), Math.min.apply(null, lngs)], [Math.max.apply(null, lats), Math.max.apply(null, lngs)]], { color: l.color, weight: 2, fillColor: l.color, fillOpacity: 0.12, interactive: false })
      rect.addTo(this.map)
      this._layerLayers[l.id] = rect
      this.$set(this.activeLayers, l.id, true)
    },
    applyDisplay() { this.sync() },
    applyIconOverride() { this.sync() },

    // ---- history playback (Stage 7) — same API as the Google engine (TrackerMap.vue) ----
    // Per-point track colour for the active colour mode (matches the real colouring tool):
    //  speed  → green(slow)→red(fast) by bucket   status → green moving / red stopped
    //  tracks → one hue per trip (default)        manual → single blue
    // Real go.lacak.io speed palette is {0:green, .5:yellow, 1:red}: slow=green, fast=red.
    trackColorFor(spd) {
      return spd <= 0 ? '#00c853' : spd < 20 ? '#7cb342' : spd < 40 ? '#ffd600' : spd < 60 ? '#fb8c00' : '#e53935'
    },
    trackColorAt(i, p, mode, tripOf) {
      if (mode === 'manual') return '#1565c0'
      if (mode === 'status') return (p.speed || 0) > 0 ? '#2e9e4b' : '#e53935'
      if (mode === 'tracks') { const ti = tripOf ? tripOf[i] : null; return ti != null ? tripColor(ti) : '#b0bec5' }
      return this.trackColorFor(p.speed || 0) // 'speed'
    },
    // divIcon arrow rotated to the fix heading (the moving playback cursor; 0° = north).
    histArrowIcon(heading) {
      const html = '<div class="lhist-arrow" style="transform:rotate(' + (heading || 0) + 'deg)">'
        + '<svg viewBox="0 0 24 24" width="26" height="26"><path d="M12 2 L19 21 L12 16 L5 21 Z" fill="#1565c0" stroke="#fff" stroke-width="1.4" stroke-linejoin="round"/></svg></div>'
      return this.L.divIcon({ className: 'lhist-arrow-wrap', html, iconSize: [26, 26], iconAnchor: [13, 13] })
    },
    // A direction chevron rotated to the fix heading (the real l-track-point arrow glyph).
    chevronIcon(heading) {
      const html = '<div style="transform:rotate(' + (heading || 0) + 'deg)"><svg viewBox="0 0 24 24" width="15" height="15">'
        + '<path d="M 12.5,2 L 16,22 L 12.5,18 L 8,22 z" fill="rgba(0,0,0,0.5)"/></svg></div>'
      return this.L.divIcon({ className: 'lhist-chevron', html, iconSize: [15, 15], iconAnchor: [7.5, 7.5] })
    },
    // A teardrop pin (the real l-track-marker path) with an inner glyph — start / finish.
    teardropIcon(color, glyph) {
      const html = '<svg viewBox="0 0 26 54" width="24" height="50">'
        + '<path d="M13,0.5C6,0.5,0.4,6.1,0.4,13.1C0.4,22.5,13,36.5,13,36.5s12.6-14,12.6-23.4C25.6,6.1,20,0.5,13,0.5z" fill="' + safeColor(color) + '" stroke="#fff" stroke-width="1.2"/>'
        + '<circle cx="13" cy="13" r="7" fill="#fff"/><text x="13" y="17" font-size="11" font-family="Material Icons" text-anchor="middle" fill="' + safeColor(color) + '">' + glyph + '</text></svg>'
      return this.L.divIcon({ className: 'lhist-pin', html, iconSize: [24, 50], iconAnchor: [12, 50] })
    },
    // Redraw the direction chevrons at even pixel spacing for the current zoom (real
    // normalizeDirectionPoints: offset ~50px, repeat ~60–130px by zoom; hidden below zoom 3).
    renderHistChevrons() {
      const L = this.L
      if (!L || !this.map || !this._histChevronLayer) return
      this._histChevronLayer.clearLayers()
      const pts = this._histChevronPts
      if (!pts || pts.length < 2) return
      const z = this.map.getZoom()
      if (z < 3) return
      const gap = ({ 12: 100, 11: 80, 10: 80, 9: 75 })[z] || (z >= 4 && z <= 8 ? 60 : 130)
      const bounds = this.map.getBounds().pad(0.2)
      let last = null
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i]
        if (!bounds.contains([p.lat, p.lng])) continue
        const sp = this.map.latLngToLayerPoint([p.lat, p.lng])
        if (last && sp.distanceTo(last) < gap) continue
        last = sp
        L.marker([p.lat, p.lng], { icon: this.chevronIcon(p.heading), interactive: false, keyboard: false }).addTo(this._histChevronLayer)
      }
    },
    // Draw a full historical track. points = [{lat,lng,speed,heading,time}].
    // opts = { colorMode, trips, stops, showStops, fit }. 'speed' mode renders as a leaflet-hotline
    // gradient; the discrete modes (tracks/status/manual) merge consecutive same-colour points into
    // one polyline (identical segmentation to the Google engine).
    showHistoryTrack(points, opts) {
      const L = this.L
      this.clearHistoryTrack()
      if (!L || !this.map || !points || points.length < 2) return
      const o = opts || {}
      const mode = o.colorMode || 'speed'
      this.histPts = points
      // point index → trip index, for the 'tracks' colour mode.
      const tripOf = new Array(points.length).fill(null)
      if (Array.isArray(o.trips)) o.trips.forEach((t, ti) => { for (let k = t.startIdx; k <= t.endIdx && k < points.length; k++) tripOf[k] = ti })

      // Draw the line as consecutive runs split on GPS quality: FINE runs (precision ≤ 65 m) render
      // as the speed-coloured hotline / colour-segmented polyline; COARSE runs (LBS / precision > 65)
      // render as a dashed grey "no data" line. Each run includes the previous boundary point so the
      // line stays continuous. (Real regular-track.segmentedPoints.)
      const isCoarse = (p) => (p.precision || 0) > 65 || p.gsm_lbs
      let ri = 0
      while (ri < points.length) {
        const coarse = isCoarse(points[ri])
        let rj = ri
        while (rj < points.length && isCoarse(points[rj]) === coarse) rj++
        const from = ri > 0 ? ri - 1 : ri // share the boundary point with the previous run
        if (rj - from >= 2) {
          if (coarse) {
            const l = L.polyline(points.slice(from, rj).map((p) => [p.lat, p.lng]), { color: '#9e9e9e', weight: 5, opacity: 0.85, dashArray: '6,10', interactive: false })
            l.addTo(this.map); this._histLayers.push(l)
          } else if (mode === 'speed') {
            const data = []
            for (let k = from; k < rj; k++) data.push([points[k].lat, points[k].lng, Math.max(0, points[k].speed || 0)])
            const hl = L.hotline(data, { min: 0, max: 60, weight: 6, outlineWidth: 1, outlineColor: 'rgba(0,0,0,0.35)', palette: SPEED_PALETTE })
            hl.addTo(this.map); this._histLayers.push(hl)
          } else {
            let curColor = null; let curPath = []
            const flush = () => { if (curPath.length > 1) { const l = L.polyline(curPath.slice(), { color: curColor, weight: 6, opacity: 0.9 }); l.addTo(this.map); this._histLayers.push(l) } }
            for (let k = from; k < rj; k++) {
              const ll = [points[k].lat, points[k].lng]
              const c = this.trackColorAt(k, points[k], mode, tripOf)
              if (curColor === null) { curColor = c; curPath = [ll] } else if (c === curColor) { curPath.push(ll) } else { curPath.push(ll); flush(); curColor = c; curPath = [ll] }
            }
            flush()
          }
        }
        ri = rj
      }
      // Accuracy circles on the imprecise (coarse / LBS) fixes — radius = the fix's precision.
      points.forEach((p) => {
        if ((p.precision || 0) > 65) {
          const c = L.circle([p.lat, p.lng], { radius: p.precision, color: '#78909c', weight: 1, opacity: 0.45, fillColor: '#90a4ae', fillOpacity: 0.12, interactive: false })
          c.addTo(this.map); this._histLayers.push(c)
        }
      })
      // Direction chevrons along the fine track, re-thinned to even screen spacing on every zoom.
      this._histChevronPts = points.filter((p) => !isCoarse(p)).map((p) => ({ lat: p.lat, lng: p.lng, heading: p.heading || 0 }))
      this._histChevronLayer = L.layerGroup().addTo(this.map); this._histLayers.push(this._histChevronLayer)
      // Redraw chevrons after any pan/zoom (moveend, NOT zoomend — a fit that only pans, or a pan
      // to a new area, must re-thin/reveal chevrons too). The initial call covers a fit:false
      // recolour redraw, where no move fires. Both are cheap (clear + ~40 markers).
      this.renderHistChevrons()
      this._onHistZoom = () => this.renderHistChevrons()
      this.map.on('moveend', this._onHistZoom)
      // Optional stop markers (Show stops).
      if (o.showStops && Array.isArray(o.stops)) {
        o.stops.forEach((s) => {
          const m = L.circleMarker([s.lat, s.lng], { radius: 5, color: '#fff', weight: 2, fillColor: '#546e7a', fillOpacity: 1, interactive: false })
          m.addTo(this.map); this._histLayers.push(m)
        })
      }
      // Start / finish teardrop pins with info popups (address · datetime · precision · mileage).
      const a = points[0]; const b = points[points.length - 1]
      const trips = Array.isArray(o.trips) ? o.trips : []
      const startAddr = trips.length ? trips[0].startAddress : ''
      const endAddr = trips.length ? trips[trips.length - 1].endAddress : ''
      const pinPopup = (title, p, addr) => '<div class="tracko-pop"><strong>' + esc(title) + '</strong>'
        + (addr ? '<div>' + esc(addr) + '</div>' : '')
        + '<div>' + esc(new Date(p.time).toLocaleString()) + '</div>'
        + '<div>±' + Math.round(p.precision || 0) + ' m' + (p.mileage != null ? ' · ' + p.mileage.toFixed(1) + ' km' : '') + '</div></div>'
      const start = L.marker([a.lat, a.lng], { icon: this.teardropIcon('#2e9e4b', 'play_arrow'), zIndexOffset: 800 }).bindPopup(pinPopup('Start', a, startAddr))
      const end = L.marker([b.lat, b.lng], { icon: this.teardropIcon('#e53935', 'flag'), zIndexOffset: 800 }).bindPopup(pinPopup('Finish', b, endAddr))
      start.addTo(this.map); end.addTo(this.map)
      this._histLayers.push(start, end)
      this.histMarker = L.marker([a.lat, a.lng], { icon: this.histArrowIcon(a.heading || 0), zIndexOffset: 900, interactive: false })
      this.histMarker.addTo(this.map); this._histLayers.push(this.histMarker)
      // Fit only on the first draw — a recolour/settings redraw must keep the user's current view.
      if (o.fit !== false) this.map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [60, 60] })
    },
    // Fit the map to a single trip's bounds (used when a trip card is clicked).
    fitTrackRange(startIdx, endIdx) {
      const L = this.L; if (!L || !this.map || !this.histPts.length) return
      const pts = []
      for (let i = startIdx; i <= endIdx && i < this.histPts.length; i++) { const p = this.histPts[i]; if (p) pts.push([p.lat, p.lng]) }
      if (pts.length) this.map.fitBounds(L.latLngBounds(pts), { padding: [60, 60] })
    },
    // Move the playback cursor to point index i.
    setHistoryPosition(i) {
      const p = this.histPts[i]; if (!p || !this.histMarker) return
      this.histMarker.setLatLng([p.lat, p.lng])
      this.histMarker.setIcon(this.histArrowIcon(p.heading || 0))
    },
    clearHistoryTrack() {
      // Drop the chevron redraw listener before tearing down the layers it redraws into.
      if (this._onHistZoom && this.map) { this.map.off('moveend', this._onHistZoom) }
      this._onHistZoom = null; this._histChevronLayer = null; this._histChevronPts = null
      if (this._histLayers && this.map) this._histLayers.forEach((l) => { try { this.map.removeLayer(l) } catch (e) { /* already gone */ } })
      this._histLayers = []
      this.histMarker = null
      this.histPts = []
      this.clearEventMarkers()
    },
    // ---- event markers (the events-layer, driven by the EVENTS tab's eye toggles) ----
    // list = [{ id, lat, lng, icon, color, title, sub, time, address }] (already enriched by the
    // panel). Redraws the whole layer each call (clear + add) — the set is small.
    showEventMarkers(list) {
      const L = this.L; if (!L || !this.map) return
      if (!this._eventLayer) this._eventLayer = L.layerGroup().addTo(this.map)
      this._eventLayer.clearLayers()
      ;(list || []).forEach((e) => {
        if (!e || !Number.isFinite(e.lat) || !Number.isFinite(e.lng)) return
        const html = '<div class="lev-pin" style="background:' + safeColor(e.color) + '"><i class="material-icons">' + esc(e.icon) + '</i></div>'
        const icon = L.divIcon({ className: 'lev-pin-wrap', html, iconSize: [28, 28], iconAnchor: [14, 28] })
        const m = L.marker([e.lat, e.lng], { icon, zIndexOffset: 700 })
        m.bindPopup('<div class="tracko-pop"><strong>' + esc(e.title) + '</strong><div>' + esc(e.sub) + (e.time ? ' · ' + esc(e.time) : '') + '</div>' + (e.address ? '<div>' + esc(e.address) + '</div>' : '') + '</div>')
        m.addTo(this._eventLayer)
      })
    },
    clearEventMarkers() { if (this._eventLayer) this._eventLayer.clearLayers() }
  }
}
</script>

<style scoped>
/* isolate: contains Leaflet's internal pane z-indexes (tilePane 200 … popupPane 700) inside
   this subtree so they can't leak into .online-screen and paint over sibling app panels
   (Objects / Widgets / travel-history). Without it, the marker pane (z600) covered the
   history panel (z15) once Leaflet became the default engine. The Google engine's container
   isolates on its own; this makes the Leaflet root behave the same. */
.lmap-root { position: absolute; inset: 0; isolation: isolate; }
.lmap { position: absolute; inset: 0; }
.lmap-root.picking .lmap { cursor: crosshair; }
.lmode-bar { position: absolute; top: 12px; left: 50%; transform: translateX(-50%); z-index: 640; display: flex; align-items: center; gap: 8px; background: #263238; color: #fff; padding: 8px 12px 8px 16px; border-radius: 20px; font-size: 13px; box-shadow: 0 2px 10px rgba(0,0,0,.3); }
.lmode-x { background: none; border: none; color: rgba(255,255,255,.8); cursor: pointer; display: flex; padding: 0; }
.lmode-x .material-icons { font-size: 18px; }
.map-ctrl { position: absolute; right: 12px; z-index: 620; display: flex; flex-direction: column; background: #fff; border-radius: 6px; box-shadow: 0 1px 5px rgba(0,0,0,.3); overflow: hidden; }
.map-ctrl-top { top: 12px; }
.map-ctrl-mid { top: 264px; }
.map-ctrl-bottom { bottom: 24px; }
.mp-sect { padding: 8px 16px 2px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #90a4ae; }
.mp-hit { display: flex; align-items: center; gap: 8px; padding: 8px 16px; font-size: 14px; color: #37474f; cursor: pointer; }
.mp-hit:hover { background: #eef6fb; }
.hit-ico { font-size: 18px; color: #90a4ae; flex: none; }
.mp-btn { display: flex; align-items: center; gap: 8px; width: calc(100% - 24px); margin: 8px 12px; padding: 8px 12px; background: #f2f6f9; border: none; border-radius: 6px; font-size: 14px; color: #37474f; cursor: pointer; }
.mp-btn .material-icons { font-size: 18px; color: #1e88c7; }
.mp-btn:hover { background: #e8f1f7; }
.fav-row { justify-content: space-between; }
.fav-name { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fav-del { background: none; border: none; color: #b0bec5; cursor: pointer; display: flex; padding: 0; }
.fav-del .material-icons { font-size: 18px; }
.mp-input { margin: 8px 14px 6px; padding: 8px 10px; width: calc(100% - 28px); box-sizing: border-box; border: 1px solid #cfd8dc; border-radius: 6px; font-size: 14px; outline: none; }
.mp-input:focus { border-color: #1e88c7; }
.lpoi-form { position: absolute; top: 16px; left: 50%; transform: translateX(-50%); z-index: 645; width: 260px; background: #fff; border-radius: 8px; box-shadow: 0 3px 16px rgba(0,0,0,.28); padding-bottom: 10px; }
.lpoi-form-head { display: flex; align-items: center; gap: 8px; padding: 12px 14px; font-size: 15px; font-weight: 500; color: #263238; border-bottom: 1px solid #eceff1; }
.lpoi-form-head .material-icons { font-size: 20px; color: #1e88c7; }
.lpoi-coord { padding: 0 14px 6px; font-size: 12px; color: #90a4ae; }
.lpoi-actions { display: flex; justify-content: flex-end; gap: 8px; padding: 0 14px; }
.poi-cancel, .poi-save { border: none; border-radius: 6px; padding: 7px 14px; font-size: 13px; cursor: pointer; }
.poi-cancel { background: #eceff1; color: #546e7a; }
.poi-save { background: #1e88c7; color: #fff; }
.mc-btn { background: #fff; border: none; width: 40px; height: 40px; cursor: pointer; color: #5f6368; display: flex; align-items: center; justify-content: center; }
.mc-btn.on { color: #1e88c7; background: #e8f4fb; }
.map-panel { position: absolute; top: 12px; right: 58px; width: 250px; background: #fff; border-radius: 8px; box-shadow: 0 2px 12px rgba(0,0,0,.22); z-index: 630; padding-bottom: 8px; }
.mp-head { display: flex; align-items: center; gap: 8px; padding: 12px 14px; font-size: 16px; font-weight: 500; color: #263238; border-bottom: 1px solid #eceff1; }
.mp-head span { flex: 1; }
.mp-x { background: none; border: none; cursor: pointer; color: #90a4ae; display: flex; }
.mp-row { display: flex; align-items: center; gap: 10px; padding: 9px 16px; font-size: 14px; color: #37474f; cursor: pointer; }
.mp-row:hover { background: #f6fafb; }
.mp-radio { width: 16px; height: 16px; border-radius: 50%; border: 2px solid #b0bec5; box-sizing: border-box; }
.mp-radio.sel { border-color: #1e88c7; background: radial-gradient(#1e88c7 40%, transparent 45%); }
.mp-sub { padding: 8px 16px 2px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #90a4ae; border-top: 1px solid #eceff1; margin-top: 4px; }
.mp-check { display: flex; align-items: center; gap: 8px; padding: 7px 16px; font-size: 14px; color: #37474f; cursor: pointer; }
.mp-swatch { width: 12px; height: 12px; border-radius: 3px; flex: none; }
.mp-empty { padding: 14px 16px; font-size: 13px; color: #90a4ae; }
.mp-search { margin: 8px 14px 4px; padding: 7px 10px; width: calc(100% - 28px); box-sizing: border-box; border: 1px solid #cfd8dc; border-radius: 6px; font-size: 13px; outline: none; }
.mp-search:focus { border-color: #1e88c7; }
.lmap-err { position: absolute; top: 12px; left: 12px; z-index: 640; background: #fff3e0; color: #e65100; padding: 8px 12px; border-radius: 6px; font: 13px Roboto, sans-serif; }
</style>

<!-- NOT scoped: marker divIcon HTML is injected dynamically (outside the scoped template),
     so it would never match [data-v-*] scoped selectors. -->
<style>
.lmarker { background: transparent; border: 0; }
.lmarker-wrap { position: relative; }
.lmarker-wrap svg { position: relative; z-index: 1; display: block; }
/* the pulsing "backlight" behind the selected marker — the real go.lacak.io veil recipe */
.lmarker-veil { position: absolute; left: 16px; top: 16px; width: 64px; height: 64px; border-radius: 50%; background: rgba(30,150,220,.125); transform: translate(-50%,-50%); animation: tracko-enlarge 1.2s linear .2s infinite; pointer-events: none; z-index: 0; }
@keyframes tracko-enlarge { 0% { transform: translate(-50%,-50%) scale(.4); } 100% { transform: translate(-50%,-50%) scale(1); } }
/* saved-place (POI) pin + label — injected divIcon HTML, so styled non-scoped like the markers */
.lpoi { background: transparent; border: 0; }
.lref { background: transparent; border: 0; }
.lpoi-label { position: absolute; left: 13px; top: 36px; transform: translateX(-50%); white-space: nowrap; font: 600 11px Roboto, Arial, sans-serif; color: #37474f; text-shadow: 0 1px 2px rgba(255,255,255,.9); pointer-events: none; }
.tracko-pop { font: 13px Roboto, Arial, sans-serif; color: #37474f; }
.tracko-pop strong { display: block; margin-bottom: 2px; color: #263238; }
/* history playback cursor — the moving heading arrow (divIcon, so styled non-scoped) */
.lhist-arrow-wrap { background: transparent; border: 0; }
.lhist-arrow { width: 26px; height: 26px; line-height: 0; filter: drop-shadow(0 1px 2px rgba(0,0,0,.4)); }
/* direction chevrons + start/finish teardrop pins (history track), injected divIcon HTML */
.lhist-chevron { background: transparent; border: 0; line-height: 0; }
.lhist-pin { background: transparent; border: 0; line-height: 0; filter: drop-shadow(0 1px 2px rgba(0,0,0,.35)); }
/* event-layer markers — a coloured teardrop badge with the event's type icon */
.lev-pin-wrap { background: transparent; border: 0; }
.lev-pin { width: 28px; height: 28px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.4); }
.lev-pin .material-icons { transform: rotate(45deg); font-size: 15px; color: #fff; line-height: 1; }
</style>
